/**
 * Utility functions for handling catalogical specifications
 * These functions help extract and parse specification values from the catalogical format
 */

export interface CatalogicalSpecification {
  key: string;
  value: any;
}

export interface AcmeConfiguration {
  isEnabled: boolean;
  windowsDeepLinkUrl: string | null;
  macOsDeepLinkUrl: string | null;
}

export type KnownSpecificationKeys =
  | "requiresLicenseCapValidation"
  | "isReturnable"
  | "isAcmeEnabledProduct"
  | "windowsDeepLinkUrl"
  | "macOsDeepLinkUrl";

/**
 * Safely extracts a specification value by key
 * @param specifications - Array of specifications from catalogical item
 * @param key - The specification key to look for
 * @param defaultValue - Default value if specification is not found
 * @returns The specification value or default value
 */
export const getSpecificationValue = (
  specifications: CatalogicalSpecification[] | undefined,
  key: string,
  defaultValue?: any
): any => {
  if (!specifications || !Array.isArray(specifications)) {
    return defaultValue;
  }

  const spec = specifications.find((spec) => spec.key === key);
  return spec?.value ?? defaultValue;
};

/**
 * Extracts a boolean specification value
 * @param specifications - Array of specifications from catalogical item
 * @param key - The specification key to look for
 * @param defaultValue - Default boolean value if specification is not found
 * @returns Boolean value from specification
 */
export const getBooleanSpecification = (
  specifications: CatalogicalSpecification[] | undefined,
  key: string,
  defaultValue: boolean = false
): boolean => {
  const value = getSpecificationValue(specifications, key);

  // Handle string "true"/"false" values and actual boolean values
  if (typeof value === "string") {
    return value.toLowerCase() === "true";
  }

  if (typeof value === "boolean") {
    return value;
  }

  return defaultValue;
};

/**
 * Extracts ACME configuration from catalogical specifications
 * @param specifications - Array of specifications from catalogical item
 * @returns ACME configuration object
 */
export const extractAcmeConfiguration = (specifications: CatalogicalSpecification[] | undefined): AcmeConfiguration => {
  const acme = {
    isEnabled: getBooleanSpecification(specifications, "isAcmeEnabledProduct", false),
    windowsDeepLinkUrl: getSpecificationValue(specifications, "windowsDeepLinkUrl", null),
    macOsDeepLinkUrl: getSpecificationValue(specifications, "macOsDeepLinkUrl", null),
  };

  return acme;
};

/**
 * Merges ACME configuration from specifications with legacy acme object for backward compatibility
 * @param specifications - Array of specifications from catalogical item
 * @param legacyAcme - Legacy acme object from ITMPv1 format
 * @returns Merged ACME configuration with specifications taking priority
 */
export const mergeAcmeConfiguration = (
  specifications: CatalogicalSpecification[] | undefined,
  legacyAcme?: { isEnabled?: boolean; windowsDeepLinkUrl?: string | null; macOsDeepLinkUrl?: string | null }
): AcmeConfiguration => {
  const specAcme = extractAcmeConfiguration(specifications);

  // Specifications take priority, fall back to legacy values if specifications are not present
  return {
    isEnabled: specAcme.isEnabled || legacyAcme?.isEnabled || false,
    windowsDeepLinkUrl: specAcme.windowsDeepLinkUrl || legacyAcme?.windowsDeepLinkUrl || null,
    macOsDeepLinkUrl: specAcme.macOsDeepLinkUrl || legacyAcme?.macOsDeepLinkUrl || null,
  };
};
